// exportUtils — browser-side CSV and JSON download helpers.
// These run entirely in the browser with no third-party service.

/** Trigger a browser download of arbitrary text content. */
function downloadText(content: string, filename: string, mimeType: string) {
  const blob = new Blob([content], { type: mimeType });
  const url  = URL.createObjectURL(blob);
  const a    = document.createElement('a');
  a.href     = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/** Convert an array of objects to a CSV string. */
function toCsv(rows: Record<string, any>[], columns: string[]): string {
  const escape = (v: any): string => {
    if (v === null || v === undefined) return '';
    const s = String(v).replace(/"/g, '""');
    return /[",\n\r]/.test(s) ? `"${s}"` : s;
  };
  const header = columns.map(escape).join(',');
  const body   = rows.map(row => columns.map(c => escape(row[c])).join(',')).join('\n');
  return header + '\n' + body;
}

// ─── Users CSV ────────────────────────────────────────────────────────────────

export interface ExportableUser {
  full_name:        string | null;
  email:            string | null;
  org_name:         string;
  roles:            string;
  status:           string;
  subscription:     string;
  created_at:       string;
  last_login_at:    string | null;
}

export function exportUsersAsCsv(users: ExportableUser[], filename = 'users-export.csv') {
  const cols: (keyof ExportableUser)[] = [
    'full_name','email','org_name','roles','status','subscription','created_at','last_login_at',
  ];
  const csv = toCsv(users, cols);
  downloadText(csv, filename, 'text/csv;charset=utf-8;');
}

// ─── Audit Log CSV ────────────────────────────────────────────────────────────

export interface ExportableAuditEntry {
  created_at:   string;
  actor_email:  string;
  action:       string;
  target_type:  string | null;
  target_id:    string | null;
  org_name:     string;
  details:      string;
}

export function exportAuditAsCsv(entries: ExportableAuditEntry[], filename = 'audit-log-export.csv') {
  const cols: (keyof ExportableAuditEntry)[] = [
    'created_at','actor_email','action','target_type','target_id','org_name','details',
  ];
  const csv = toCsv(entries, cols);
  downloadText(csv, filename, 'text/csv;charset=utf-8;');
}

// ─── User Data Report (JSON) ─────────────────────────────────────────────────

export interface UserDataReport {
  generated_at: string;
  profile:      any;
  organization: any;
  roles:        any[];
  scenarios:    any[];
  assignments:  any[];
  audit_entries: any[];
}

export function exportUserDataReport(report: UserDataReport, email: string) {
  const json = JSON.stringify(report, null, 2);
  const slug = email.replace(/[^a-z0-9]/gi, '_').toLowerCase();
  downloadText(json, `user-data-report-${slug}.json`, 'application/json');
}
